import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Database, TrendingUp, TrendingDown, AlertCircle, CheckCircle, AlertTriangle, AlertOctagon, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('dqp_token')}` } });

const fadeUp = { hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

function QualityBadge({ score }) {
  if (score >= 95) return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Excellent</span>;
  if (score >= 85) return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">Good</span>;
  if (score >= 70) return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Fair</span>;
  return <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">Poor</span>;
}

function AnomalyCard({ anomaly }) {
  const severityConfig = {
    critical: { bg: 'bg-red-50', border: 'border-red-200', icon: AlertOctagon, color: '#ef4444' },
    high: { bg: 'bg-orange-50', border: 'border-orange-200', icon: AlertTriangle, color: '#f97316' },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertCircle, color: '#f59e0b' },
    low: { bg: 'bg-blue-50', border: 'border-blue-200', icon: AlertCircle, color: '#3b82f6' },
  };

  const config = severityConfig[anomaly.severity] || severityConfig.low;
  const Icon = config.icon;

  return (
    <div className={`${config.bg} border ${config.border} rounded-lg p-3 flex gap-3`}>
      <Icon size={18} style={{ color: config.color }} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[#1e1e2e]">{anomaly.table}</p>
        <p className="text-xs text-[#6b7280] mt-0.5">{anomaly.message}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [datasources, setDatasources] = useState([]);
  const [selectedDatasourceId, setSelectedDatasourceId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadDatasources = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/datasources`, authH());
      setDatasources(data);
      if (data.length > 0 && !selectedDatasourceId) {
        setSelectedDatasourceId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load datasources', err);
    }
  }, [selectedDatasourceId]);

  const loadAnalytics = useCallback(async () => {
    if (!selectedDatasourceId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dashboard/data-analytics/${selectedDatasourceId}`, authH());
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDatasourceId]);

  useEffect(() => {
    loadDatasources();
  }, [loadDatasources]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (!analytics) return (
    <DashboardLayout>
      <div className="p-6 md:p-8 text-center">
        <p className="text-[#6b7280]">{loading ? 'Loading analytics...' : 'No data source selected'}</p>
      </div>
    </DashboardLayout>
  );

  const { overall_metrics, tables, anomalies, datasource } = analytics;

  // Prepare table chart data
  const tableChartData = tables.map(t => ({
    name: t.name,
    rows: t.row_count,
    quality: t.quality_score,
  }));

  // Quality score indicator
  const getQualityColor = (score) => {
    if (score >= 95) return '#10b981';
    if (score >= 85) return '#3b82f6';
    if (score >= 70) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          {/* Header */}
          <motion.div variants={fadeUp} className="mb-8">
            <h1 className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
              Data Analytics
            </h1>
            <p className="text-[#6b7280] text-sm mt-1">
              Insights from {datasource?.name || 'your database'}
            </p>
          </motion.div>

          {/* Datasource Selector */}
          {datasources.length > 1 && (
            <motion.div variants={fadeUp} className="mb-6">
              <label className="block text-sm font-medium text-[#1e1e2e] mb-2">Select Data Source</label>
              <select
                value={selectedDatasourceId || ''}
                onChange={(e) => setSelectedDatasourceId(e.target.value)}
                className="w-full md:w-64 px-3 py-2 border border-[#e6e8f2] rounded-lg text-sm focus:outline-none focus:border-[#5b63ea]"
              >
                {datasources.map(ds => (
                  <option key={ds.id} value={ds.id}>{ds.name}</option>
                ))}
              </select>
            </motion.div>
          )}

          {/* Overall KPIs */}
          <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-[#e6e8f2] p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#5b63ea12]">
                  <Database size={18} style={{ color: '#5b63ea' }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
                {(overall_metrics?.total_rows / 1000).toFixed(1)}K
              </p>
              <p className="text-xs text-[#9ca3af] mt-1">Total Records</p>
            </div>

            <div className="bg-white rounded-xl border border-[#e6e8f2] p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: getQualityColor(overall_metrics?.quality_score) + '12' }}>
                  <CheckCircle size={18} style={{ color: getQualityColor(overall_metrics?.quality_score) }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
                {overall_metrics?.quality_score?.toFixed(1)}%
              </p>
              <p className="text-xs text-[#9ca3af] mt-1">Data Quality</p>
            </div>

            <div className="bg-white rounded-xl border border-[#e6e8f2] p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#f59e0b12]">
                  <AlertTriangle size={18} style={{ color: '#f59e0b' }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
                {overall_metrics?.null_percentage?.toFixed(1)}%
              </p>
              <p className="text-xs text-[#9ca3af] mt-1">Missing Data</p>
            </div>

            <div className="bg-white rounded-xl border border-[#e6e8f2] p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#3b82f612]">
                  <Zap size={18} style={{ color: '#3b82f6' }} />
                </div>
              </div>
              <p className="text-2xl font-bold text-[#1e1e2e]" style={{ fontFamily: 'Outfit' }}>
                {overall_metrics?.total_tables}
              </p>
              <p className="text-xs text-[#9ca3af] mt-1">Tables</p>
            </div>
          </motion.div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Table Row Count Distribution */}
            <motion.div variants={fadeUp} className="bg-white rounded-xl border border-[#e6e8f2] p-6">
              <h2 className="font-semibold text-[#1e1e2e] mb-4" style={{ fontFamily: 'Outfit' }}>Row Count by Table</h2>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tableChartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e6e8f2" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e6e8f2' }} />
                    <Bar dataKey="rows" fill="#5b63ea" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            {/* Quality Score by Table */}
            <motion.div variants={fadeUp} className="bg-white rounded-xl border border-[#e6e8f2] p-6">
              <h2 className="font-semibold text-[#1e1e2e] mb-4" style={{ fontFamily: 'Outfit' }}>Data Quality by Table</h2>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={tableChartData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e6e8f2" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e6e8f2' }} />
                    <Line type="monotone" dataKey="quality" stroke="#10b981" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>

          {/* Data Quality Heatmap Table */}
          <motion.div variants={fadeUp} className="bg-white rounded-xl border border-[#e6e8f2] p-6 mb-8">
            <h2 className="font-semibold text-[#1e1e2e] mb-4" style={{ fontFamily: 'Outfit' }}>Data Quality Details</h2>
            <div className="scrollbar-light overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e6e8f2]">
                    <th className="text-left py-3 px-4 font-semibold text-[#6b7280]">Table</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#6b7280]">Rows</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#6b7280]">Columns</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#6b7280]">Quality</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#6b7280]">Missing Data</th>
                    <th className="text-left py-3 px-4 font-semibold text-[#6b7280]">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tables.map((table, i) => (
                    <tr key={i} className="border-b border-[#f3f4f6] hover:bg-[#f9fafb]">
                      <td className="py-3 px-4 font-medium text-[#1e1e2e]">{table.name}</td>
                      <td className="py-3 px-4 text-[#6b7280]">{(table.row_count / 1000).toFixed(1)}K</td>
                      <td className="py-3 px-4 text-[#6b7280]">{table.column_count}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 bg-[#e6e8f2] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${table.quality_score}%`, backgroundColor: getQualityColor(table.quality_score) }} />
                          </div>
                          <span className="text-xs font-semibold w-12 text-right">{table.quality_score.toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={table.null_percentage > 10 ? 'text-amber-600 font-semibold' : 'text-emerald-600'}>
                          {table.null_percentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <QualityBadge score={table.quality_score} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Anomalies Section */}
          {anomalies.length > 0 && (
            <motion.div variants={fadeUp}>
              <h2 className="font-semibold text-[#1e1e2e] mb-4" style={{ fontFamily: 'Outfit' }}>Data Quality Alerts</h2>
              <div className="space-y-3">
                {anomalies.map((anomaly, i) => (
                  <AnomalyCard key={i} anomaly={anomaly} />
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
